import React, { useState, useEffect } from 'react';
import apiClient from '../utils/apiClient';

const BreakManager = ({ date, workingHours }) => {
    const [breaks, setBreaks] = useState([]);
    const [showAddForm, setShowAddForm] = useState(false);
    const [newBreak, setNewBreak] = useState({
        from: '',
        to: '',
        title: 'Odmor',
        isRecurring: false
    });
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [editingBreak, setEditingBreak] = useState(null);

    useEffect(() => {
        fetchBreaks();
    }, [date]);

    const fetchBreaks = async () => {
        try {
            const dateStr = date.toISOString().split('T')[0];
            const response = await apiClient.get(`/breaks/date/${dateStr}`);
            setBreaks(response.data);
        } catch (error) {
            console.error('Error fetching breaks:', error);
        }
    };

    const handleAddBreak = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');        
        
        if (!newBreak.from || !newBreak.to) {
            setError('Prosimo, izpolnite vsa obvezna polja');
            return;
        }

        // Validate minimum duration (30 minutes)
        const fromTime = new Date(`1970-01-01T${newBreak.from}:00`);
        const toTime = new Date(`1970-01-01T${newBreak.to}:00`);
        const diffMinutes = (toTime - fromTime) / (1000 * 60);        
        
        // set minimum duration of the break
        // if (diffMinutes < 30) {
        //     setError('Trajanje odmora mora biti vsaj 30 minut');
        //     return;
        // }

        if (fromTime >= toTime) {
            setError('Končni čas mora biti po začetnem času');
            return;
        }

        try {
            const breakData = {
                date: date.toISOString().split('T')[0],
                from: newBreak.from,
                to: newBreak.to,
                title: newBreak.title || 'Odmor',
                isRecurring: newBreak.isRecurring
            };

            await apiClient.post('/breaks', breakData);
              if (newBreak.isRecurring) {
                setSuccess('Ponavljajoči odmori so bili uspešno dodani za celoten teden');
            } else {
                setSuccess('Odmor je bil uspešno dodan');
            }
            
            setNewBreak({ from: '', to: '', title: 'Odmor', isRecurring: false });
            setShowAddForm(false);
            await fetchBreaks();
        } catch (error) {
            if (error.response?.data?.error) {
                setError(error.response.data.error);
                if (error.response.data.conflicts) {
                    const conflictList = error.response.data.conflicts.map(
                        c => `${c.date}: ${c.booking}`
                    ).join(', ');
                    setError(`${error.response.data.error}: ${conflictList}`);
                }            
            } else {
                setError('Dodajanje odmora ni uspelo');
            }
        }
    };

    const handleEditBreak = async (breakItem) => {
        setEditingBreak({
            ...breakItem,
            originalId: breakItem.id
        });
    };

    const handleUpdateBreak = async () => {
        setError('');
        setSuccess('');

        try {
            await apiClient.put(`/breaks/${editingBreak.originalId}`, {
                from: editingBreak.from,
                to: editingBreak.to,
                title: editingBreak.title
            });

            setSuccess('Odmor je bil uspešno posodobljen');
            setEditingBreak(null);
            await fetchBreaks();
        } catch (error) {
            if (error.response?.data?.error) {
                setError(error.response.data.error);            
            } else {
                setError('Posodabljanje odmora ni uspelo');
            }
        }
    };

    const handleDeleteBreak = async (breakId, isRecurring, from, to) => {        
        
        if (!window.confirm('Ali ste prepričani, da želite izbrisati ta odmor?')) {
            return;
        }

        setError('');
        setSuccess('');

        try {
            if (isRecurring) {                
                const confirmWeek = window.confirm(
                    'To je ponavljajoči odmor. Ali želite izbrisati vse odmore za ta časovni razpon za celoten teden?'
                );
                
                if (confirmWeek) {
                    await apiClient.delete('/breaks/week/bulk', {
                        data: {
                            date: date.toISOString().split('T')[0],
                            from,
                            to
                        }
                    });
                    setSuccess('Vsi ponavljajoči odmori za teden so bili uspešno izbrisani');
                } else {
                    await apiClient.delete(`/breaks/${breakId}`);
                    setSuccess('Odmor je bil uspešno izbrisan');
                }
            } else {
                await apiClient.delete(`/breaks/${breakId}`);
                setSuccess('Odmor je bil uspešno izbrisan');
            }

            await fetchBreaks();        
        } catch (error) {
            setError('Brisanje odmora ni uspelo');
        }
    };

    const formatTime = (timeStr) => {
        return timeStr.length === 5 ? timeStr : `0${timeStr}`.slice(-5);
    };    
    
    const getWorkingHoursDisplay = () => {
        if (!workingHours.from || !workingHours.to) return 'Ni nastavljeno';
        return `${workingHours.from} - ${workingHours.to}`;
    };

    return (
        <div className="break-manager">            
            <div className="break-manager-header">
                <h4>Odmori za {date.toLocaleDateString('sl-SI', { weekday: 'long', month: 'short', day: 'numeric' })}</h4>
                <p className="working-hours-info">Delovni čas: {getWorkingHoursDisplay()}</p>
                {/* Show header 'Dodaj odmor' button when the add form is closed so user can open it */}
                {workingHours.from && workingHours.to && !showAddForm && (
                    <button
                        type="button"
                        className="btn-add-break"
                        onClick={() => setShowAddForm(true)}
                    >
                        Dodaj odmor
                    </button>
                )}
            </div>

            {error && <div className="error-message">{error}</div>}
            {success && <div className="success-message">{success}</div>}

            {showAddForm && (
                <form onSubmit={handleAddBreak} className="add-break-form">                    
                    <div className="form-row">
                        <div className="time-range-container" role="group" aria-label="Čas odmora">
                            <input
                                type="time"
                                className="time-input"
                                aria-label="Začetek odmora"
                                value={newBreak.from}
                                onChange={(e) => setNewBreak({ ...newBreak, from: e.target.value })}
                                min={workingHours.from}
                                max={workingHours.to}
                                required
                            />
                            <span className="time-separator">–</span>
                            <input
                                type="time"
                                className="time-input"
                                aria-label="Konec odmora"
                                value={newBreak.to}
                                onChange={(e) => setNewBreak({ ...newBreak, to: e.target.value })}
                                min={workingHours.from}
                                max={workingHours.to}
                                required
                            />
                        </div>
                    </div>
                    <div className="form-group">
                        <label>Razlog:</label>
                        <input
                            type="text"
                            value={newBreak.title}
                            onChange={(e) => setNewBreak({ ...newBreak, title: e.target.value })}
                            placeholder="Odmor"
                        />
                    </div>
                    <div className="form-group">
                        <label className="checkbox-label">
                            <input
                                type="checkbox"
                                checked={newBreak.isRecurring}
                                onChange={(e) => setNewBreak({ ...newBreak, isRecurring: e.target.checked })}
                            />
                            <span>Uporabi za celoten teden</span>
                        </label>
                    </div>
                    <div className="form-buttons">
                        <button type="submit" className="btn-save">Dodaj odmor</button>
                        <button type="button" onClick={() => setShowAddForm(false)} className="btn-cancel">Prekliči</button>
                    </div>
                </form>
            )}

            <div className="breaks-list">                
                {breaks.length === 0 ? (
                    <p className="no-breaks">Za ta dan ni načrtovanih odmorov</p>
                ) : (
                    breaks.map((breakItem) => (
                        <div key={breakItem.id} className="break-item">
                            {editingBreak?.originalId === breakItem.id ? (
                                <div className="edit-break-form">
                                    <div className="form-row">
                                        <div className="time-range-container" role="group" aria-label="Uredi čas odmora">
                                            <input
                                                type="time"
                                                className="time-input"
                                                aria-label="Uredi začetek odmora"
                                                value={editingBreak.from}
                                                onChange={(e) => setEditingBreak({ ...editingBreak, from: e.target.value })}
                                                min={workingHours.from}
                                                max={workingHours.to}
                                            />
                                            <span className="time-separator">–</span>
                                            <input
                                                type="time"
                                                className="time-input"
                                                aria-label="Uredi konec odmora"
                                                value={editingBreak.to}
                                                onChange={(e) => setEditingBreak({ ...editingBreak, to: e.target.value })}
                                                min={workingHours.from}
                                                max={workingHours.to}
                                            />
                                        </div>
                                    </div>
                                    <input
                                        type="text"
                                        value={editingBreak.title}
                                        onChange={(e) => setEditingBreak({ ...editingBreak, title: e.target.value })}
                                        placeholder="Naslov odmora"
                                    />                                    
                                    <div className="edit-buttons">
                                        <button onClick={handleUpdateBreak} className="btn-save">Shrani</button>
                                        <button onClick={() => setEditingBreak(null)} className="btn-cancel">Prekliči</button>
                                    </div>
                                </div>
                            ) : (
                                <div className="break-info">
                                    <div className="break-details">
                                        <span className="break-time">{formatTime(breakItem.from)} - {formatTime(breakItem.to)}</span>
                                        <span className="break-title">{breakItem.title}</span>
                                        {breakItem.isRecurring && <span className="recurring-badge">Ponavljajoče</span>}
                                    </div>                                    <div className="break-actions">
                                        <button onClick={() => handleEditBreak(breakItem)} className="btn-edit">Uredi</button>
                                        <button 
                                            onClick={() => handleDeleteBreak(breakItem.id, breakItem.isRecurring, breakItem.from, breakItem.to)} 
                                            className="btn-delete"
                                        >
                                            Izbriši
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default BreakManager;
